import styled, { css } from "styled-components";
import { CardContainer } from "../Card/Card.styles";

export const RoomBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--space-2);
`;

export const RoundButton = styled.button`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: var(--radius-full);
  background: var(--card);
  color: var(--ink);
  border: var(--border-w) solid var(--ink);
  box-shadow: 0 3px 0 var(--line-strong);
  cursor: pointer;
  transition: transform var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);

  &:active {
    transform: translateY(3px);
    box-shadow: 0 0 0 var(--line-strong);
  }
`;

export const CodeSticker = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 44px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-full);
  background: var(--gold);
  color: var(--ink);
  border: var(--border-w) solid var(--ink);
  box-shadow: 0 var(--lip) 0 var(--gold-shade);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: var(--text-lg);
  letter-spacing: 0.2em;
  cursor: pointer;
  rotate: -2deg;
  transition: transform var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);

  small {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: 800;
    letter-spacing: 0;
    opacity: 0.7;
  }

  &:active {
    transform: translateY(var(--lip));
    box-shadow: 0 0 0 var(--gold-shade);
  }
`;

export const QRCodeFrame = styled.div`
  display: grid;
  place-items: center;
  gap: var(--space-3);
  text-align: center;

  > div {
    padding: var(--space-4);
    background: white;
    border: var(--border-w) solid var(--ink);
    border-radius: var(--radius-lg);
    box-shadow: 0 var(--lip) 0 var(--line-strong);
  }

  svg {
    display: block;
    width: min(240px, 60vw);
    height: auto;
  }
`;

export const Controls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
`;

export const Hint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  text-align: center;
  font-weight: 700;
  color: var(--ink-soft);

  strong {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: 600;
    color: var(--ink);
  }
`;

export const Waiting = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  max-width: 22rem;
  margin: 0 auto;
  padding: var(--space-4) var(--space-5);
  text-align: center;
  font-weight: 700;
  color: var(--ink-soft);
  background: var(--card);
  border: var(--border-w) dashed var(--line-strong);
  border-radius: var(--radius-lg);
  animation: bob 2.4s ease-in-out infinite;
`;

export const TableContainer = styled.div<{ $loading?: boolean }>`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-8) var(--space-8);
  transition: opacity var(--dur-slow) var(--ease-out);
  opacity: ${({ $loading }) => ($loading ? 0.2 : 1)};

  @media screen and (max-width: 600px) {
    padding: var(--space-4) var(--space-3) var(--space-8);
  }
`;

export const AnswersContainer = styled.div<{ $show: boolean }>`
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  max-width: 100%;
  flex-wrap: wrap;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  translate: 0 ${({ $show }) => ($show ? 0 : 500)}%;
  transition:
    opacity var(--dur-slow) var(--ease-out),
    translate 500ms var(--ease-bounce);
`;

export const AnswerCards = styled.div<{
  $count: number;
  $visible?: boolean;
  $voteStarted?: boolean;
  $voteFinished?: boolean;
  $won?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  height: fit-content;
  cursor: pointer;
  border-radius: var(--radius-lg);
  -webkit-tap-highlight-color: transparent;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  translate: ${({ $visible }) => ($visible ? 0 : 500)}%
    ${({ $visible }) => ($visible ? 0 : -100)}%;
  max-width: ${({ $visible }) => ($visible ? 300 : 0)}px;
  transition:
    opacity var(--dur-slow) var(--ease-out),
    translate var(--dur-slow) var(--ease-bounce),
    max-width var(--dur-slow) var(--ease-bounce);

  min-height: calc(
    ${({ $voteStarted, $voteFinished }) =>
        ($voteFinished && "52px") || ($voteStarted && "44px") || "0"} +
      var(--card-size) / 2 + var(--card-size) / 2 * ${({ $count }) => $count}
  );

  & > div {
    transition: rotate var(--dur-slow) var(--ease-bounce);
    rotate: ${({ $visible }) => ($visible ? 360 : 0)}deg;
    -webkit-tap-highlight-color: transparent;
  }

  ${({ $won }) =>
    $won &&
    css`
      ${CardContainer} {
        --ring: var(--gold);
        border-color: var(--gold-shade);
        box-shadow: 0 var(--lip) 0 var(--gold-shade);
        animation: pulse-ring 1.4s var(--ease-out) infinite;
      }
    `}
`;

export const AnswerWrapper = styled.div<{ $open?: boolean }>`
  height: ${({ $open }) =>
    $open ? "var(--card-size)" : "calc(var(--card-size) / 2)"};
  overflow: visible;
  transition: height var(--dur-base) var(--ease-bounce);

  &:not(:first-child) {
    ${CardContainer} {
      box-shadow: ${({ $open }) =>
        $open ? "0 var(--lip) 0 var(--line-strong)" : "0 -3px 0 var(--line), 0 var(--lip) 0 var(--line-strong)"};
    }
  }
`;

export const QuestionContainer = styled.div<{ $loading?: boolean }>`
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  align-items: center;
  position: relative;

  ${CardContainer} {
    cursor: default;
    rotate: ${({ $loading }) => ($loading ? "-180deg" : "-2deg")};
    translate: 0 ${({ $loading }) => ($loading ? "-200%" : "0%")};
    transition:
      rotate var(--dur-slow) var(--ease-bounce),
      translate var(--dur-slow) var(--ease-bounce);
  }
`;
